/**
 * SyllabusScanService
 * 
 * Singleton service that manages background syllabus scan jobs.
 * Persists scan state to localStorage so scans survive SPA navigation.
 * Polls the AI service for status and emits events when complete.
 * 
 * Usage:
 *   import { syllabusScanService } from './SyllabusScanService';
 *   syllabusScanService.startScan(documentId, options);
 *   syllabusScanService.on('complete', (scanId, result) => { ... });
 */
import { AiDocumentProcessingApi } from './ai/AiDocumentProcessingApi';

const STORAGE_KEY = 'dh_syllabus_scans';
const POLL_INTERVAL_ACTIVE = 5000;   // 5s when tab is visible
const POLL_INTERVAL_HIDDEN = 15000;  // 15s when tab is hidden
const MAX_POLL_DURATION = 5 * 60 * 1000; // 5 min timeout

// Scan states
export const SCAN_STATUS = {
  PENDING: 'pending',       // Uploaded, not yet started processing
  PROCESSING: 'processing', // AI service is working
  COMPLETE: 'complete',     // Results available
  ERROR: 'error',           // Failed
};

class SyllabusScanServiceClass {
  constructor() {
    this._scans = new Map();
    this._listeners = new Map(); // eventName -> Set<callback>
    this._pollTimers = new Map();
    this._activePromises = new Map(); // scanId -> Promise (for the actual API call)
    this._initialized = false;
  }

  /**
   * Initialize from localStorage and resume any active scans.
   * Safe to call multiple times.
   */
  init() {
    if (this._initialized) return;
    this._initialized = true;

    // Load persisted scans
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        for (const [id, scan] of Object.entries(parsed)) {
          this._scans.set(id, scan);
        }
      }
    } catch (e) {
      console.warn('[SyllabusScanService] Failed to load persisted scans:', e);
    }

    // Resume polling for any scans still in processing state
    for (const [scanId, scan] of this._scans) {
      if (scan.status === SCAN_STATUS.PROCESSING || scan.status === SCAN_STATUS.PENDING) {
        // The actual API call may have completed server-side; we'll poll the result
        // For now, re-start the scan call if it was pending
        if (scan.status === SCAN_STATUS.PENDING) {
          this._executeScan(scanId);
        } else {
          // Was processing — the API call was in-flight when the page closed.
          // The AI service continues processing server-side.
          // We poll a status endpoint, or fall back to re-triggering.
          this._startPolling(scanId);
        }
      }
    }

    // Adjust poll frequency on visibility change
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        for (const scanId of this._pollTimers.keys()) {
          this._restartPolling(scanId);
        }
      });
    }
  }

  /**
   * Start a new syllabus scan.
   * @param {string} documentId - The uploaded document ID
   * @param {Object} options - { courseName }
   * @returns {string} scanId (same as documentId for simplicity)
   */
  startScan(documentId, options = {}) {
    const scanId = String(documentId);
    
    const scan = {
      scanId,
      documentId,
      status: SCAN_STATUS.PENDING,
      options,
      result: null,
      error: null,
      startedAt: Date.now(),
      completedAt: null,
    };

    this._scans.set(scanId, scan);
    this._persist();
    this._emit('start', scanId, scan);
    this._emit('change', scanId, scan);

    // Fire off the actual API call
    this._executeScan(scanId);

    return scanId;
  }

  /**
   * Execute the scan API call for a given scanId.
   */
  async _executeScan(scanId) {
    const scan = this._scans.get(scanId);
    if (!scan) return;

    // Update status
    scan.status = SCAN_STATUS.PROCESSING;
    this._persist();
    this._emit('progress', scanId, { status: SCAN_STATUS.PROCESSING, message: 'Analyzing your syllabus...' });
    this._emit('change', scanId, scan);

    // Race the API call against a timeout so a hung server can't block forever
    const SCAN_TIMEOUT = 3 * 60 * 1000; // 3 minutes
    const apiCall = AiDocumentProcessingApi.processDocumentAsSyllabus(
      scan.documentId,
      scan.options
    );
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('Syllabus processing timed out')), SCAN_TIMEOUT);
    });

    try {
      const response = await Promise.race([apiCall, timeoutPromise]);
      clearTimeout(timeoutId);

      const data = await response.json();

      scan.status = SCAN_STATUS.COMPLETE;
      scan.result = data;
      scan.completedAt = Date.now();
      this._persist();
      this._emit('complete', scanId, data);
      this._emit('change', scanId, scan);
      this._stopPolling(scanId);

    } catch (error) {
      clearTimeout(timeoutId);
      // Suppress unhandled rejection from the orphaned API promise on timeout
      apiCall.catch(() => {});

      console.error('[SyllabusScanService] Scan failed:', error);
      scan.status = SCAN_STATUS.ERROR;
      scan.error = error.message || 'Scan failed';
      scan.completedAt = Date.now();
      this._persist();
      this._emit('error', scanId, error);
      this._emit('change', scanId, scan);
      this._stopPolling(scanId);
    }
  }

  /**
   * Start polling the status endpoint for a scan that was in-flight when the page closed.
   * The AI service continues processing server-side; we poll until complete or timeout.
   */
  _startPolling(scanId) {
    const scan = this._scans.get(scanId);
    if (!scan) return;

    const elapsed = Date.now() - scan.startedAt;
    if (elapsed > MAX_POLL_DURATION) {
      // Too old — mark as error
      scan.status = SCAN_STATUS.ERROR;
      scan.error = 'Scan timed out';
      scan.completedAt = Date.now();
      this._persist();
      this._emit('error', scanId, new Error('Scan timed out'));
      this._emit('change', scanId, scan);
      return;
    }

    // Poll the lightweight status endpoint
    this._pollOnce(scanId);
  }

  /**
   * Poll the scan status endpoint once and schedule next poll if still processing.
   */
  async _pollOnce(scanId) {
    const scan = this._scans.get(scanId);
    if (!scan || scan.status === SCAN_STATUS.COMPLETE || scan.status === SCAN_STATUS.ERROR) return;

    try {
      const statusData = await AiDocumentProcessingApi.getSyllabusScanStatus(scan.documentId);
      
      if (statusData.status === 'complete') {
        // Fetch the full result
        try {
          const response = await AiDocumentProcessingApi.processDocumentAsSyllabus(
            scan.documentId,
            scan.options
          );
          const data = await response.json();
          scan.status = SCAN_STATUS.COMPLETE;
          scan.result = data;
          scan.completedAt = Date.now();
        } catch (fetchErr) {
          // Status says complete but fetch failed — use summary from status
          scan.status = SCAN_STATUS.COMPLETE;
          scan.result = { summary: statusData.summary, status: 'completed' };
          scan.completedAt = Date.now();
        }
        this._persist();
        this._emit('complete', scanId, scan.result);
        this._emit('change', scanId, scan);
        this._stopPolling(scanId);
        return;
      }

      if (statusData.status === 'error') {
        scan.status = SCAN_STATUS.ERROR;
        scan.error = 'Scan failed on server';
        scan.completedAt = Date.now();
        this._persist();
        this._emit('error', scanId, new Error('Scan failed on server'));
        this._emit('change', scanId, scan);
        this._stopPolling(scanId);
        return;
      }

      // Still processing — schedule next poll
      this._scheduleNextPoll(scanId);
    } catch (err) {
      console.warn('[SyllabusScanService] Poll failed, will retry:', err);
      this._scheduleNextPoll(scanId);
    }
  }

  /**
   * Schedule the next poll, adjusting interval based on tab visibility.
   */
  _scheduleNextPoll(scanId) {
    this._stopPolling(scanId); // clear any existing timer
    const interval = (typeof document !== 'undefined' && document.hidden)
      ? POLL_INTERVAL_HIDDEN
      : POLL_INTERVAL_ACTIVE;
    const timerId = setTimeout(() => this._pollOnce(scanId), interval);
    this._pollTimers.set(scanId, timerId);
  }

  _restartPolling(scanId) {
    const scan = this._scans.get(scanId);
    if (scan && (scan.status === SCAN_STATUS.PROCESSING || scan.status === SCAN_STATUS.PENDING)) {
      this._scheduleNextPoll(scanId);
    }
  }

  _stopPolling(scanId) {
    if (this._pollTimers.has(scanId)) {
      clearInterval(this._pollTimers.get(scanId));
      this._pollTimers.delete(scanId);
    }
  }

  /**
   * Get scan state for a specific scanId.
   */
  getScan(scanId) {
    return this._scans.get(String(scanId)) || null;
  }

  /**
   * Get all active (non-complete) scans.
   */
  getActiveScans() {
    const active = [];
    for (const scan of this._scans.values()) {
      if (scan.status === SCAN_STATUS.PENDING || scan.status === SCAN_STATUS.PROCESSING) {
        active.push(scan);
      }
    }
    return active;
  }

  /**
   * Remove a completed or errored scan from storage.
   */
  clearScan(scanId) {
    this._scans.delete(String(scanId));
    this._stopPolling(String(scanId));
    this._persist();
  }

  /**
   * Clear all completed scans.
   */
  clearCompleted() {
    for (const [id, scan] of this._scans) {
      if (scan.status === SCAN_STATUS.COMPLETE || scan.status === SCAN_STATUS.ERROR) {
        this._scans.delete(id);
      }
    }
    this._persist();
  }

  // --- Event system ---

  on(event, callback) {
    if (!this._listeners.has(event)) {
      this._listeners.set(event, new Set());
    }
    this._listeners.get(event).add(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    this._listeners.get(event)?.delete(callback);
  }

  _emit(event, ...args) {
    const listeners = this._listeners.get(event);
    if (listeners) {
      for (const cb of listeners) {
        try { cb(...args); } catch (e) { console.error('[SyllabusScanService] Listener error:', e); }
      }
    }
    // Also fire a DOM event for cross-component communication
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(`syllabusscan:${event}`, { detail: { scanId: args[0], data: args[1] } }));
    }
  }

  // --- Persistence ---

  _persist() {
    try {
      const obj = {};
      for (const [id, scan] of this._scans) {
        obj[id] = scan;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
      console.warn('[SyllabusScanService] Failed to persist:', e);
    }
  }
}

// Singleton
export const syllabusScanService = new SyllabusScanServiceClass();

// Auto-init
syllabusScanService.init();
